import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense, useMemo } from 'react';

import { _getAdminViewsUserStats } from '@/api/admin/queries';

import { makeDummyEmptyData } from '../../analytics-tab';
import { CardProps, UserCardId, userChartCards } from '../../analytics-tab/common';
import { ChartsCarousel, ChartsCarouselFallback } from './charts-carousel';

const DAYS_IN_WEEK = 7;

const UsersSummaryCarousel = () => {
  const { data: userStats } = useSuspenseQuery(_getAdminViewsUserStats());

  const last30Dummy = useMemo(() => makeDummyEmptyData(30), []);

  const cardsWithData = useMemo(() => {
    const enhancers: Partial<Record<UserCardId, (_c: CardProps) => CardProps>> = {
      newUsers: (c) => ({
        ...c,
        value: userStats.newUsersLast7Days,
        delta: `~${(userStats.newUsersLast7Days / DAYS_IN_WEEK).toFixed(1)} users/day`,
        chartData: userStats.dailyRegistrationsLast7Days,
      }),

      totalUsers: (c) => ({
        ...c,
        value: userStats.totalUsers,
        delta: `~${userStats.newUsersLast30Days} users/30 days`,
        chartData: userStats.dailyRegistrationsLast30DaysCumulative,
      }),

      newVerifiedUsers: (c) => ({
        ...c,
        value: userStats.verifiedUsersLast7Days,
        chartData: userStats.dailyVerificationsLast7Days,
      }),

      totalVerifiedUsers: (c) => ({
        ...c,
        value: userStats.totalKycVerifiedUsers,
        delta: `~${userStats.kycVerifiedUsersLast30Days} users/30 days`,
        chartData: userStats.dailyKycVerificationsLast30DaysCumulative,
      }),
    };

    return userChartCards.map((c) => enhancers[c.id]?.(c) ?? { ...c, chartData: last30Dummy });
  }, [userStats, last30Dummy]);

  return <ChartsCarousel cards={cardsWithData} />;
};

export const UserTabSummaryCarousel = () => {
  return (
    <Suspense fallback={<ChartsCarouselFallback cards={userChartCards} />}>
      <UsersSummaryCarousel />
    </Suspense>
  );
};
